import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Square, CheckSquare, Trash2 } from "lucide-react";
import { AssigneePicker } from "./AssigneePicker";

const PRIORITY_DOT = { High: "bg-priority-high", Medium: "bg-priority-medium", Low: "bg-priority-low" };
const BUCKET_LABEL = { overdue: "Overdue", today: "Today", tomorrow: "Tomorrow", this_week: "This week" };

/** One inline-editable task row — title/notes/assignee/due date/priority/status, no modals
 * anywhere. Only one field is ever "open" for edit at a time (click a field -> it becomes the
 * live control, Enter/blur saves, Esc reverts). Arrow-key row-to-row navigation is handled by
 * the parent list (this row just exposes a focusable, tabIndex=0 wrapper via `rowRef`). */
export function TaskRow({ task, principals, showAssignee, onToggleStatus, onPatch, onDelete, rowRef }) {
  const [editing, setEditing] = useState(null); // null | "title" | "notes" | "due" | "priority" | "assignee"
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const startEdit = (field, initial) => { setDraft(initial ?? ""); setEditing(field); };
  const cancelEdit = () => setEditing(null);
  const saveEdit = (field, value) => { onPatch(task.id, { [field]: value }); setEditing(null); };

  const onKeyDownField = (field, value) => (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(field, value); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  const done = task.status === "Done";

  return (
    <div
      ref={rowRef}
      tabIndex={0}
      data-testid={`task-row-${task.id}`}
      className="flex items-start gap-3 px-[var(--row-px,16px)] py-[var(--row-py,12px)] border-b border-hairline hover:bg-surface2/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand"
    >
      <button
        data-testid={`task-toggle-status-${task.id}`}
        onClick={() => onToggleStatus(task)}
        title={done ? "Reopen" : "Mark done"}
        aria-label={done ? "Reopen task" : "Mark task done"}
        className={`mt-0.5 shrink-0 ${done ? "text-emerald-600" : "text-faint hover:text-emerald-600"}`}
      >
        {done ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
      </button>

      <div className="min-w-0 flex-1 space-y-1">
        {editing === "title" ? (
          <input
            ref={inputRef} autoFocus data-testid={`task-title-input-${task.id}`}
            value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDownField("title", draft)} onBlur={() => saveEdit("title", draft)}
            className="w-full rounded-md border border-hairline bg-paper px-2 py-1 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
        ) : (
          <button
            data-testid={`task-title-${task.id}`} onClick={() => startEdit("title", task.title)}
            className={`block text-left text-[length:var(--row-text,14px)] w-full truncate ${done ? "line-through text-faint" : "text-ink"}`}
          >
            {task.title}
          </button>
        )}

        {editing === "notes" ? (
          <textarea
            autoFocus rows={2} data-testid={`task-notes-input-${task.id}`}
            value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDownField("notes", draft)} onBlur={() => saveEdit("notes", draft)}
            className="w-full rounded-md border border-hairline bg-paper px-2 py-1 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
          />
        ) : (
          <button data-testid={`task-notes-${task.id}`} onClick={() => startEdit("notes", task.notes)} className="block text-left text-[11px] text-faint hover:text-ink-soft truncate">
            {task.notes || "Add notes…"}
          </button>
        )}

        <div className="flex items-center gap-2 flex-wrap text-[11px] text-faint">
          {showAssignee && editing !== "assignee" && (
            <button data-testid={`task-assignee-${task.id}`} onClick={() => setEditing("assignee")} className="hover:text-ink-soft">
              {task.assignee?.name || "Unassigned"}
            </button>
          )}
          {editing === "assignee" && (
            <AssigneePicker
              principals={principals} value={task.assignee} autoFocus testId={`task-assignee-picker-${task.id}`}
              onChange={(p) => saveEdit("assignee", p)}
            />
          )}

          {task.project_name && (
            <Link to={`/projects/${task.project_id}`} className="hover:text-brand" onClick={(e) => e.stopPropagation()}>{task.project_name}</Link>
          )}

          {editing === "due" ? (
            <input
              type="date" autoFocus data-testid={`task-due-input-${task.id}`}
              defaultValue={task.due_date || ""}
              onChange={(e) => saveEdit("due_date", e.target.value || null)}
              onBlur={cancelEdit} onKeyDown={(e) => e.key === "Escape" && cancelEdit()}
              className="rounded-md border border-hairline bg-paper px-1.5 py-0.5 text-[11px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
          ) : (
            <button data-testid={`task-due-${task.id}`} onClick={() => setEditing("due")} className={task.date_bucket === "overdue" ? "text-red-600 font-medium hover:underline" : "hover:text-ink-soft"}>
              {task.due_date ? `${BUCKET_LABEL[task.date_bucket] || "Due"} ${task.due_date}` : "Set due date"}
            </button>
          )}
        </div>
      </div>

      {editing === "priority" ? (
        <select
          autoFocus data-testid={`task-priority-select-${task.id}`} value={task.priority}
          onChange={(e) => saveEdit("priority", e.target.value)} onBlur={cancelEdit}
          className="rounded-md border border-hairline bg-paper px-1.5 py-1 text-[11px] text-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
        >
          <option>High</option><option>Medium</option><option>Low</option>
        </select>
      ) : (
        <button data-testid={`task-priority-dot-${task.id}`} onClick={() => setEditing("priority")} title={`Priority: ${task.priority}`} aria-label={`Priority: ${task.priority}. Click to change.`} className="mt-1.5 shrink-0">
          <span className={`h-2 w-2 rounded-full block ${PRIORITY_DOT[task.priority] || PRIORITY_DOT.Medium}`} />
        </button>
      )}

      <button data-testid={`task-delete-${task.id}`} onClick={() => onDelete(task.id)} aria-label="Delete task" className="mt-0.5 shrink-0 text-hairline hover:text-red-500">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
